import React, { useEffect, useState, useRef } from "react";
import { useParams } from "react-router-dom";
import { QRCodeSVG } from "qrcode.react";
import { databases, storage } from "./appwrite";
import { Printer, Download, FileText } from "lucide-react"; // Added FileText icon
import { toPng } from "html-to-image";
import jsPDF from "jspdf"; // Import jsPDF

const DB_ID = process.env.REACT_APP_APPWRITE_DB_ID || "regdb";
const COL_ID =
  process.env.REACT_APP_APPWRITE_COLLECTION_ID || "692a9f82002dabe4ac9c";
const BUCKET_ID = "mmttcregid";

export default function IdentityCard() {
  const { userId } = useParams();
  const [user, setUser] = useState(null);
  const [photoUrl, setPhotoUrl] = useState(null);
  const [loading, setLoading] = useState(true);
  const cardRef = useRef(null);

  useEffect(() => {
    (async () => {
      try {
        const doc = await databases.getDocument(DB_ID, COL_ID, userId);
        setUser(doc);
        if (doc.photoFileId) {
          const url = storage.getFileView(BUCKET_ID, doc.photoFileId, 200, 200);

          const img = new Image();
          img.src = url;
          img.onload = () => setPhotoUrl(url);
        }
      } catch {
        alert("Invalid ID");
      } finally {
        setLoading(false);
      }
    })();
  }, [userId]);

  // --- DOWNLOAD AS PNG ---
  const handleDownloadImage = async () => {
    if (!cardRef.current) return;
    try {
      const dataUrl = await toPng(cardRef.current, {
        cacheBust: true,
        pixelRatio: 5,
        backgroundColor: "#ffffff",
      });
      const link = document.createElement("a");
      link.download = `${user.name}_ID.png`;
      link.href = dataUrl;
      link.click();
    } catch (err) {
      console.error(err);
    }
  };

  // --- DOWNLOAD AS PDF (Exact Size) ---
  const handleDownloadPDF = async () => {
    if (!cardRef.current) return;
    try {
      // 1. Capture high-res image
      const dataUrl = await toPng(cardRef.current, {
        cacheBust: true,
        pixelRatio: 5,
        backgroundColor: "#ffffff",
      });

      // 2. Calculate dimensions
      const imgProps = cardRef.current.getBoundingClientRect();
      const pdfWidth = imgProps.width * 0.264583; // Convert px to mm (1px = 0.264583mm)
      const pdfHeight = imgProps.height * 0.264583;

      // 3. Create PDF with exact card dimensions
      const pdf = new jsPDF({
        orientation: "portrait",
        unit: "mm",
        format: [pdfWidth, pdfHeight], // Dynamic page size
      });

      // 4. Add image to PDF (0 margin)
      pdf.addImage(dataUrl, "PNG", 0, 0, pdfWidth, pdfHeight);
      pdf.save(`${user.name}_ID.pdf`);
    } catch (err) {
      console.error("PDF generation failed", err);
      alert("Could not generate PDF.");
    }
  };

  if (loading)
    return (
      <div className="h-screen flex items-center justify-center">
        Loading ID...
      </div>
    );
  if (!user) return null;

  return (
    <div className="min-h-screen bg-slate-100 flex flex-col items-center justify-center p-4 print:bg-white print:p-0">
      {/* ACTION BUTTONS */}
      <div className="flex flex-wrap gap-3 mb-8 print:hidden">
        <button
          onClick={handleDownloadImage}
          className="flex items-center gap-2 bg-white text-indigo-700 border border-indigo-200 px-5 py-3 rounded-xl font-semibold shadow hover:bg-indigo-50 transition-colors"
        >
          <Download size={18} /> PNG
        </button>

        {/* NEW PDF BUTTON */}
        <button
          onClick={handleDownloadPDF}
          className="flex items-center gap-2 bg-red-600 text-white px-5 py-3 rounded-xl font-semibold shadow hover:bg-red-700 transition-colors"
        >
          <FileText size={18} /> PDF
        </button>

        <button
          onClick={() => window.print()}
          className="flex items-center gap-2 bg-indigo-900 text-white px-5 py-3 rounded-xl font-semibold shadow hover:bg-indigo-800 transition-colors"
        >
          <Printer size={18} /> Print
        </button>
      </div>

      {/* CARD */}
      <div
        ref={cardRef}
        className="relative w-[390px] rounded-2xl bg-white shadow-2xl overflow-hidden border print:shadow-none print:border-0"
      >
        {/* WATERMARK */}
        <img
          src="/logo1.png"
          alt="LOGO"
          className="absolute opacity-[0.05] w-[500px] h-[420px] top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 pointer-events-none"
          crossOrigin="anonymous"
        />

        {/* HEADER */}
        <div className="relative bg-gradient-to-r from-indigo-800 to-blue-900 px-4 pt-4 pb-14">
          <div className="flex items-center gap-4 justify-center">
            <img
              src="/logo.png"
              alt="LOGO"
              className="absolute top-3 left-1 w-12 h-15  p-1 invert"
              crossOrigin="anonymous"
            />

            <div className="leading-tight text-center">
              <p className="text-[12px] text-indigo-100 font-display font-bold tracking-wide uppercase">
                Skill Incubation Innovation
              </p>
              <p className="text-[12px] text-indigo-100 font-display font-bold tracking-wide uppercase">
                Entrepreneurship Development Centre
              </p>
              <p className="text-[10px] text-indigo-100 font-display tracking-widest">
                UNIVERSITY OF JAMMU
              </p>
            </div>
          </div>
        </div>

        {/* BODY */}
        <div className="relative px-4 pb-4 text-center">
          {/* PHOTO */}
          <div className="-mt-12 mx-auto w-28 h-28 rounded-full border-4 border-white bg-white shadow overflow-hidden">
            {photoUrl === null ? (
              <img
                src="/user.png"
                alt=""
                className="mt-3 h-full w-full object-cover"
              />
            ) : photoUrl ? (
              <img
                src={photoUrl}
                alt=""
                className="w-full h-full object-cover"
              />
            ) : (
              <span className="text-3xl font-bold text-indigo-700 flex h-full items-center justify-center">
                {user.name[0]}
              </span>
            )}
          </div>

          {/* NAME */}
          <h1 className="mt-1 text-xl font-bold tracking-wide text-gray-900 leading-tight uppercase">
            {user.name}
          </h1>
          <p className="text-indigo-700 text-sm font-semibold mt-1">
            {user.designation?.replace(/\b\w/g, (c) => c.toUpperCase())}
          </p>
          <p className="text-xs text-gray-500 mt-0.5">
            {user.department?.replace(/\b\w/g, (c) => c.toUpperCase())}
          </p>
          <p className="text-xs text-gray-500 mt-0.5 ">
            {user.affiliation?.replace(/\b\w/g, (c) => c.toUpperCase())}
          </p>

          {/* INFO */}
          <div className="mt-3 bg-white-300 rounded-lg p-3 text-xs space-y-1.5 text-left border border-slate-300 shadow-lg inline-block">
            <div className="border-l-2 border-blue-600 pl-1 space-y-1 font-mono">
              <InfoRow
                label="Unique ID:"
                value={user.$id.slice(0, 10).toLowerCase()}
              />

              <InfoRow
                label="Event &nbsp; &nbsp;:"
                value={`5-Days Capacity Building Program`}
              />

              <InfoRow label="Validity :" value="19th to 23rd January, 2026" />
            </div>
          </div>

          {/* VERIFIED RIBBON */}

          <div className="absolute top-3 right-3 text-white text-[10px] px-2 py-1 flex items-center gap-3">
            <img
              src={`https://bwipjs-api.metafloor.com/?bcid=code128&text=${user.$id
                .slice(0, 10)
                .toLowerCase()}&includetext&scale=PixelRatio.get()&width=150&background=ffffff&foreground=none`}
              alt="barcode"
              className="h-5 w-128"
            />
          </div>

          {/* QR */}

          <div className="mt-2 flex flex-col items-center">
            <span className="text-[9px] tracking-widest text-gray-400 font-display">
              SCAN FOR AUTHENTICITY
            </span>

            <div className="p-1 bg-white border rounded">
              <QRCodeSVG
                size={84}
                level="H"
                value={`${window.location.origin}/verify/${user.$id}`}
              />
            </div>
            <div className="mt-3 text-center leading-snug">
              <p className="text-[9px] text-gray-500 font-display">
                Digitally signed by
              </p>
              <p className="text-[11px] text-blue-700 font-semibold font-display uppercase">
                Dr. Jatinder Manhas
              </p>
              <p className="text-[9px] text-gray-600 font-display">
                Course Coordinator
              </p>
            </div>
          </div>
        </div>

        {/* FOOTER */}
        <div className="bg-indigo-900 px-4 py-2 text-center">
          <div className="mx-auto w-1/2 border-t border-indigo-400/30 mb-1" />
          <p className="text-[10px] text-indigo-300 tracking-wider font-medium font-display">
            Organized by MMTTC & SIIEDC, University of Jammu
          </p>
          <p className="text-[8px] text-indigo-300/60 tracking-wide font-light font-display mt-0.5">
            For queries call 84920 13757 | 94191 73793
          </p>
        </div>
      </div>
    </div>
  );
}

function InfoRow({ label, value, mono }) {
  return (
    <div className="flex justify-between gap-3 items-start">
      <span className="text-gray-500 font-semibold font-mono min-w-[90px]">
        {label}
      </span>
      <span
        className={`text-gray-900 text-right ${
          mono ? "font-mono" : "font-mono"
        } truncate w-full`}
      >
        {value}
      </span>
    </div>
  );
}
